import Button, { ButtonVariants } from '@/components/atoms/Button/Button';
import { DEFAULT_COMPARISON_EV_DETAILS, RESET } from '@/lib/constants';
import { GetCustomOrderModelsForMakeResponse } from '@/lib/schema/models/types';
import { EVDetailProps } from '@/types/compare-ev';
import { EVCompareMakeSelection } from '../EVCompareMakeSelection/EVCompareMakeSelection';
import { EVCompareModelHeader } from '../EVCompareModelHeader/EVCompareModelHeader';
import { EVCompareModelSelection } from '../EVCompareModelSelection/EVCompareModelSelection';
import {
  EVCompareTrimSelection,
  TrimListProps,
} from '../EVCompareTrimSelection/EVCompareTrimSelection';

interface EVCompareModelProps {
  makes: string[];
  selectedEVDefaultDetails: EVDetailProps;
  handleMakeClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  handleMakeChange: () => void;
  handleBadgeClick: (year: string) => void;
  handleModelClick: (model: string, imageURL: string, index: number) => void;
  handleReset?: () => void;
  makeEVOptions: GetCustomOrderModelsForMakeResponse;
  yearList: string[];
  activeModelIndex: number;
  trimList: TrimListProps[];
  handleTrimClick: (trim: string, MSRP: number, index: number) => void;
  activeTrimIndex: number;
  handleSelectEVClick?: () => void;
  modelHeader: string;
  modelButtonLabel: string;
  selectedFirstEVStyle?: string;
  selectedSecondEVStyle?: string;
  firstEVIconStyle?: string;
  secondEVIconStyle?: string;
  showComparisonDetailPage?: boolean;
  selectedFirstEVDetails?: EVDetailProps;
  selectedSecondEVDetails?: EVDetailProps;
  onCompleteFlow: () => Promise<void>;
  showReset: boolean;
  editEVAlertMessage?: string;
  selectedEditEVIndex?: number;
  showHeader?: boolean;
}

export const EVCompareModel: React.FC<EVCompareModelProps> = ({
  makes,
  selectedEVDefaultDetails,
  handleMakeClick,
  handleMakeChange,
  handleBadgeClick,
  handleModelClick,
  makeEVOptions,
  yearList,
  activeModelIndex,
  trimList,
  handleTrimClick,
  activeTrimIndex,
  handleSelectEVClick,
  handleReset,
  modelHeader,
  modelButtonLabel,
  selectedFirstEVStyle = '',
  selectedSecondEVStyle = '',
  secondEVIconStyle = '',
  firstEVIconStyle = '',
  showComparisonDetailPage,
  selectedFirstEVDetails = DEFAULT_COMPARISON_EV_DETAILS,
  selectedSecondEVDetails = DEFAULT_COMPARISON_EV_DETAILS,
  onCompleteFlow,
  showReset,
  editEVAlertMessage = '',
  selectedEditEVIndex,
  showHeader = true,
}) => {
  const atLastStep =
    showComparisonDetailPage || selectedEditEVIndex !== undefined;
  return (
    <>
      {showHeader && (
        <EVCompareModelHeader
          modelHeader={modelHeader}
          selectedFirstEVStyle={selectedFirstEVStyle}
          selectedSecondEVStyle={selectedSecondEVStyle}
          firstEVIconStyle={firstEVIconStyle}
          secondEVIconStyle={secondEVIconStyle}
          showComparisonDetailPage={showComparisonDetailPage}
          selectedFirstEVDetails={selectedFirstEVDetails}
          selectedSecondEVDetails={selectedSecondEVDetails}
        />
      )}
      <section className="item-start h-full w-full overflow-scroll pb-xl">
        <EVCompareMakeSelection
          makes={makes}
          selectedMake={selectedEVDefaultDetails.make}
          handleMakeClick={handleMakeClick}
          handleMakeChange={handleMakeChange}
        />
        {selectedEVDefaultDetails.make !== '' && (
          <EVCompareModelSelection
            yearList={yearList}
            handleBadgeClick={handleBadgeClick}
            makeEVOptions={makeEVOptions}
            selectedYear={selectedEVDefaultDetails.year}
            selectedMake={selectedEVDefaultDetails.make}
            activeModelIndex={activeModelIndex}
            handleModelClick={handleModelClick}
            showComparisonDetailPage={showComparisonDetailPage}
            selectedEditEVIndex={selectedEditEVIndex}
          />
        )}
        {selectedEVDefaultDetails.model !== '' && (
          <EVCompareTrimSelection
            trimList={trimList}
            handleTrimClick={handleTrimClick}
            activeTrimIndex={activeTrimIndex}
          />
        )}
      </section>
      <section className="absolute bottom-0 flex w-full items-center justify-between overflow-hidden border-t border-neutral-300 bg-white pr-[30px] l:pr-[60px]">
        {showReset && (
          <div
            className="cursor-pointer text-body1Medium text-neutral-800 hover:underline"
            onClick={handleReset}
          >
            {RESET}
          </div>
        )}
        <div className="flex w-[30%] items-center text-[clamp(0.5rem,1vw,0.75rem)] text-red-500 m:w-[40%]">
          {editEVAlertMessage}
        </div>
        <div className="my-l">
          <Button
            variant={ButtonVariants.Tertiary}
            onClick={atLastStep ? onCompleteFlow : handleSelectEVClick}
            disabled={selectedEVDefaultDetails.trim === ''}
            aria-label="select compare evs"
          >
            {modelButtonLabel}
          </Button>
        </div>
      </section>
    </>
  );
};
